import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonTextSizes,
  buttonFunctions,
} from '../../shared/Button';
import Table from '../../shared/table/Table';
import { selectorTypes } from '../../shared/table/TableRow';
import SvgXIcon from '../../svg/XIcon';
import {
  submitText,
  cancelText,
  nameText,
  unitNameText,
  searchText,
} from '../../shared/SharedInternationalizations';
import HelpIcon from '../../shared/HelpIcon';
import InlineHelpText from '../../shared/InlineHelpText';
import permissions from '../../../permissionsList';
import AccessControl from '../../../AccessControl';
import { required } from '../../../utilities/general';
import { Condition } from '../../shared/Condition';

/**
 * The component to add or edit a new unit
 * @param {function} handleSubmit - The function to be run when the form is submitted
 * @param {array} unitArray - The array of all units
 * @param {object} parent_unit - The unit object for the parent unit of the current unit
 * @param {object} unit - The unit object for the current unit
 * @returns {object} The contents of the form
 */
export let UnconnectedAddEditUnitForm = (props) => {
  const { onSubmit, unitArray, parent_unit, unit = { path: '' } } = props;
  const pathArray = unit.path.split(',');
  const pathArrInt = pathArray.map((i) => parseInt(i));
  const hasParentUnit = parent_unit ? true : '';
  const [parentFilter, setParentFilter] = useState('');

  const [showUnitNameHelpText, setShowUnitNameHelpText] = useState(false);
  const unitNameHelpTextRef = useRef();
  function toggleUnitNameHelpText(value) {
    setShowUnitNameHelpText(value);
  }
  const unitNameHelpText = (
    <FormattedMessage
      id="addEditUnitForm.unitNameHelpText"
      defaultMessage="The name for this unit. Each unit name must be unique."
      description="Help text."
    />
  );
  const [showAddress1HelpText, setShowAddress1HelpText] = useState(false);
  const address1HelpTextRef = useRef();
  function toggleAddress1HelpText(value) {
    setShowAddress1HelpText(value);
  }
  const address1HelpText = (
    <FormattedMessage
      id="addEditUnitForm.address1HelpText"
      defaultMessage="The primary part of the address for this unit. Usually this would be the number and street name of the unit."
      description="Help text."
    />
  );
  const [showAddress2HelpText, setShowAddress2HelpText] = useState(false);
  const address2HelpTextRef = useRef();
  function toggleAddress2HelpText(value) {
    setShowAddress2HelpText(value);
  }
  const address2HelpText = (
    <FormattedMessage
      id="addEditUnitForm.address2HelpText"
      defaultMessage="The second part of the address for this unit. Usually this would be information such as the suite number or other information."
      description="Help text."
    />
  );
  const [showCityHelpText, setShowCityHelpText] = useState(false);
  const cityHelpTextRef = useRef();
  function toggleCityHelpText(value) {
    setShowCityHelpText(value);
  }
  const cityHelpText = (
    <FormattedMessage
      id="addEditUnitForm.cityHelpText"
      defaultMessage="The city this unit is in."
      description="Help text."
    />
  );
  const [showStateHelpText, setShowStateHelpText] = useState(false);
  const stateHelpTextRef = useRef();
  function toggleStateHelpText(value) {
    setShowStateHelpText(value);
  }
  const stateHelpText = (
    <FormattedMessage
      id="addEditUnitForm.stateHelpText"
      defaultMessage="The state this unit is in."
      description="Help text."
    />
  );
  const [showZipcodeHelpText, setShowZipcodeHelpText] = useState(false);
  const zipcodeHelpTextRef = useRef();
  function toggleZipcodeHelpText(value) {
    setShowZipcodeHelpText(value);
  }
  const zipcodeHelpText = (
    <FormattedMessage
      id="addEditUnitForm.zipcodeHelpText"
      defaultMessage="The zipcode for this unit."
      description="Help text."
    />
  );
  const [
    showDisplayParentUnitHelpText,
    setShowDisplayParentUnitHelpText,
  ] = useState(false);
  const displayParentUnitHelpTextRef = useRef();
  function toggleDisplayParentUnitHelpText(value) {
    setShowDisplayParentUnitHelpText(value);
  }
  const displayParentUnitHelpText = (
    <FormattedMessage
      id="addEditUnitForm.displayParentUnitHelpText"
      defaultMessage="If this unit will have a parent unit, it can be set here. A parent unit is a unit that contains one or more units inside of it. This creates a hierarchy of units such as 'Building 1' > '1st Floor' > 'North Wing' > 'Room 101'. This hierarchy allows for to be more logically organized and sorted."
      description="Help text."
    />
  );
  const [showParentUnitHelpText, setShowParentUnitHelpText] = useState(false);
  const parentUnitHelpTextRef = useRef();
  function toggleParentUnitHelpText(value) {
    setShowParentUnitHelpText(value);
  }
  const parentUnitHelpText = (
    <FormattedMessage
      id="addEditUnitForm.parentUnitHelpText"
      defaultMessage="The parent unit for this unit. Choose the unit from the table below to set the parent. Click the 'x' next to a selected unit in the Selected Unit table to remove a parent unit."
      description="Help text."
    />
  );

  const [showUnitCheckInHelpText, setShowUnitCheckInHelpText] = useState(false);
  const unitCheckInHelpTextRef = useRef();
  function toggleShowUnitCheckInHelpText(value) {
    setShowUnitCheckInHelpText(value);
  }
  const unitCheckInHelpText = (
    <FormattedMessage
      id="addEditUnitForm.userCheckInHelpText"
      defaultMessage="This unit will need to check in to SARA"
      description="Help text."
    />
  );

  /**
   * Function to set the search value for active filtering
   * @param {object} event - The event from typing in the value to the search bar
   * @fires setParentFilter - Updates local state with the value typed into the search bar
   */
  const searchFilter = (event) => {
    const value = event.target.value;
    setParentFilter(value);
  };

  /**
   * Function to generate the parent unit table
   * @returns {object} - The table of parent units
   */
  const parentUnitTable = (values) => {
    const initiallyFilteredUnits = unitArray.filter((item) => {
      let itemPathArray = [];
      if (item.path) {
        itemPathArray = item.path.split(',');
      }
      const itemPathInt = itemPathArray.map((i) => parseInt(i));
      if (item.id === unit.id) {
        return false;
      } else if (unit.path && pathArrInt.includes(item.id)) {
        if (
          values.parentUnitId &&
          (values.parentUnitId === item.id ||
            parseInt(values.parentUnitId) === item.id)
        ) {
          return false;
        } else {
          return true;
        }
      } else if (item.path && itemPathInt.includes(unit.id)) {
        return false;
      } else if (!item.is_active) {
        return false;
      } else if (values.parent_unit && values.parentUnitId.includes(item.id)) {
        return false;
      } else {
        return true;
      }
    });

    // map over the units and put into the format that is required for the table component
    const parentUnitArray = initiallyFilteredUnits.map((unit) => {
      const checked = unit.id === parent_unit ? true : null;
      return {
        selector: {
          checked: checked,
          sortBy: unit.id,
          display: unit.id,
        },
        name: {
          sortBy: unit.name.toUpperCase(),
          display: unit.name,
        },
      };
    });

    let searchedUnits = [];
    // Take the array and filter based on the text entered in the search bar.
    if (parentFilter !== '') {
      searchedUnits = parentUnitArray.filter((item) => {
        // Make the device name uppercase for case insensitivity
        const parent_unit = item.name.display.toUpperCase();
        // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
        return parent_unit.includes(parentFilter.toUpperCase());
      });
    } else {
      searchedUnits = parentUnitArray;
    }

    const tableHeadings = [
      {
        isSortable: false,
        display: '',
      },
      {
        isSortable: true,
        sortBy: 'name',
        display: nameText,
      },
    ];
    // generates the actual table
    const table = (
      <Table
        hasTableHead={true}
        hasSelector={true}
        tableHeadings={tableHeadings}
        selectorName="parentUnitId"
        selectorParamValue="id"
        sortParam="name"
        tableData={searchedUnits}
        selectorType={selectorTypes.radio}
        tableStyles="table__form-selector-table"
        selected={parent_unit}
        data-test="add-unit-parent-unit-selected-table"
      />
    );

    return table;
  };

  /**
   * Function to generate the parent unit table
   * @param {object} values - The current values entered in the form
   * @returns {object} - The table of parent units
   */
  const selectedUnitTable = (values) => {
    let table;
    let parentUnitId = values.parentUnitId;
    if (parentUnitId !== '') {
      if (typeof parentUnitId === 'string') {
        parentUnitId = parseInt(parentUnitId);
      }
      const filteredUnit = unitArray.filter((unit) => unit.id === parentUnitId);

      const unitRow = filteredUnit.map((unit) => {
        const removeButton = (
          <label>
            <Field
              className="visually-hidden"
              name="parentUnitId"
              component="input"
              type="radio"
              value=""
            />
            <SvgXIcon />
          </label>
        );
        return {
          button: {
            sortBy: '',
            display: removeButton,
          },
          name: {
            sortBy: unit.name.toUpperCase(),
            display: unit.name,
          },
        };
      });

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },
        {
          isSortable: false,

          display: (
            <FormattedMessage
              id="addEditUnitForm.parentUnitSelectedListHeading"
              defaultMessage="Selected Unit"
              description="Header to sort the unit name by"
            />
          ),
        },
      ];
      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="name"
          tableData={unitRow}
          tableStyles="table__form-selector-table"
          data-test="add-unit-parent-unit-table"
        />
      );
    }
    return table;
  };

  let usesCheckin = false;
  let hasExistingProfile = false;
  if (unit) {
    if (
      unit.has_checkin_profile &&
      unit.checkin_profile &&
      Object.keys(unit.checkin_profile).length > 0
    ) {
      usesCheckin = true;
      hasExistingProfile = true;
    }
  }

  return (
    <React.Fragment>
      <div className="add-unit-form__message-container" />
      <Form
        data-test="add-unit-form"
        onSubmit={onSubmit}
        initialValues={{
          id: unit.id,
          usesCheckin: usesCheckin,
          parentUnitId: parent_unit,
          hasParentUnit: hasParentUnit,
          name: unit.name,
          address1: unit.address1,
          address2: unit.address2,
          city: unit.city,
          state: unit.state,
          zipcode: unit.zipcode,
        }}
        render={({
          handleSubmit,
          pristine,
          invalid,
          reset,
          submitting,
          values,
        }) => (
          <form onSubmit={handleSubmit} className="width-100">
            <Field
              className="visually-hidden"
              name="id"
              component="input"
              type="text"
            />
            <div className="content-display__container">
              <AccessControl
                data-test="access-control"
                feature={permissions.static.units.addUnitCheckinProfile}>
                <React.Fragment>
                  <div className="content-display__column content-display__column--2-3">
                    <div className="content-display__item-container">
                      <div className="item-container__main-container">
                        <Field
                          className="visually-hidden"
                          name="usesCheckin"
                          component="input"
                          type="checkbox"
                          id="usesCheckin"
                          data-test="uses-checkin"
                          disabled={hasExistingProfile}
                        />
                        <label
                          htmlFor="usesCheckin"
                          className={
                            hasExistingProfile
                              ? 'text-large text-dimmed text-light form-item__field-toggle'
                              : 'text-large text-light text-deep-blue form-item__field-toggle'
                          }>
                          {hasExistingProfile ? (
                            <FormattedMessage
                              id="addEditUnitForm.unitHasCheckIn"
                              defaultMessage="This unit already has a check-in profile for SARA"
                              description="The user's title such as 'Mr', 'Ms','Dr', etc "
                            />
                          ) : (
                            <FormattedMessage
                              id="addEditUnitForm.unitUsesCheckIn"
                              defaultMessage="This unit will need to check in to SARA"
                              description="The user's title such as 'Mr', 'Ms','Dr', etc "
                            />
                          )}
                        </label>
                      </div>
                      <div className="item-container__info-container">
                        <HelpIcon
                          onClick={() =>
                            unitCheckInHelpTextRef.current.toggleHelpText()
                          }
                          toggled={showUnitCheckInHelpText}
                          styles="margin-left-5"
                        />
                      </div>
                    </div>
                    <InlineHelpText
                      ref={unitCheckInHelpTextRef}
                      toggleFunction={toggleShowUnitCheckInHelpText}
                      message={unitCheckInHelpText}
                    />
                  </div>
                  <div className="content-display__column content-display__column--1-3"></div>
                </React.Fragment>
              </AccessControl>

              <div className="content-display__column content-display__column--1-2">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <label className="item-container__main-text text-large text-light form-item__field-message--required">
                      {unitNameText}:
                    </label>
                    <Field
                      className="form-item__field"
                      name="name"
                      component="input"
                      type="text"
                      validate={required}>
                      {({ input, meta }) => (
                        <React.Fragment>
                          <input
                            {...input}
                            type="text"
                            className={
                              meta.error && meta.touched
                                ? 'form-item__field form-item__field--error'
                                : 'form-item__field'
                            }
                          />
                          {meta.error && meta.touched && (
                            <span className="form-item__field-message--required text-small">
                              {meta.error}
                            </span>
                          )}
                        </React.Fragment>
                      )}
                    </Field>
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      onClick={() =>
                        unitNameHelpTextRef.current.toggleHelpText()
                      }
                      toggled={showUnitNameHelpText}
                      styles="margin-left-5"
                    />
                  </div>
                </div>
                <InlineHelpText
                  ref={unitNameHelpTextRef}
                  toggleFunction={toggleUnitNameHelpText}
                  message={unitNameHelpText}
                />
              </div>
              <div className="content-display__column content-display__column--1-2">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <label className="item-container__main-text text-large text-light">
                      <FormattedMessage
                        id="addEditUnitForm.address1Label"
                        defaultMessage="Address 1:"
                        description="The first part of the address of the unit being created"
                      />
                    </label>
                    <Field
                      className="form-item__field"
                      name="address1"
                      component="input"
                      type="text"
                    />
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      onClick={() =>
                        address1HelpTextRef.current.toggleHelpText()
                      }
                      toggled={showAddress1HelpText}
                      styles="margin-left-5"
                    />
                  </div>
                </div>
                <InlineHelpText
                  ref={address1HelpTextRef}
                  toggleFunction={toggleAddress1HelpText}
                  message={address1HelpText}
                />
              </div>
              <div className="content-display__column content-display__column--1-2">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <label className="item-container__main-text text-large text-light">
                      <FormattedMessage
                        id="addEditUnitForm.address2Label"
                        defaultMessage="Address 2:"
                        description="The second part of the address of the unit being created"
                      />
                    </label>
                    <Field
                      className="form-item__field"
                      name="address2"
                      component="input"
                      type="text"
                    />
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      onClick={() =>
                        address2HelpTextRef.current.toggleHelpText()
                      }
                      toggled={showAddress2HelpText}
                      styles="margin-left-5"
                    />
                  </div>
                </div>
                <InlineHelpText
                  ref={address2HelpTextRef}
                  toggleFunction={toggleAddress2HelpText}
                  message={address2HelpText}
                />
              </div>
              <div className="content-display__column content-display__column--1-2">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <label className="item-container__main-text text-large text-light">
                      <FormattedMessage
                        id="addEditUnitForm.cityLabel"
                        defaultMessage="City:"
                        description="The city of the unit being created"
                      />
                    </label>
                    <Field
                      className="form-item__field"
                      name="city"
                      component="input"
                      type="text"
                    />
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      onClick={() => cityHelpTextRef.current.toggleHelpText()}
                      toggled={showCityHelpText}
                      styles="margin-left-5"
                    />
                  </div>
                </div>
                <InlineHelpText
                  ref={cityHelpTextRef}
                  toggleFunction={toggleCityHelpText}
                  message={cityHelpText}
                />
              </div>
              <div className="content-display__column content-display__column--1-2">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <label className="item-container__main-text text-large text-light">
                      <FormattedMessage
                        id="addEditUnitForm.stateLabel"
                        defaultMessage="State:"
                        description="The state of the unit being created"
                      />
                    </label>
                    <Field
                      className="form-item__field"
                      name="state"
                      component="input"
                      type="text"
                    />
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      onClick={() => stateHelpTextRef.current.toggleHelpText()}
                      toggled={showStateHelpText}
                      styles="margin-left-5"
                    />
                  </div>
                </div>
                <InlineHelpText
                  ref={stateHelpTextRef}
                  toggleFunction={toggleStateHelpText}
                  message={stateHelpText}
                />
              </div>
              <div className="content-display__column content-display__column--1-2">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <label className="item-container__main-text text-large text-light">
                      <FormattedMessage
                        id="addEditUnitForm.zipcodeLabel"
                        defaultMessage="Zipcode:"
                        description="The zipcode of the unit being created"
                      />
                    </label>
                    <Field
                      className="form-item__field"
                      name="zipcode"
                      component="input"
                      type="text"
                    />
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      onClick={() =>
                        zipcodeHelpTextRef.current.toggleHelpText()
                      }
                      toggled={showZipcodeHelpText}
                      styles="margin-left-5"
                    />
                  </div>
                </div>
                <InlineHelpText
                  ref={zipcodeHelpTextRef}
                  toggleFunction={toggleZipcodeHelpText}
                  message={zipcodeHelpText}
                />
              </div>
              <div className="content-display__column content-display__column--1-2">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <label
                      className="item-container__main-text text-large text-light"
                      htmlFor="hasParentUnit">
                      <Field
                        name="hasParentUnit"
                        id="hasParentUnit"
                        component="input"
                        type="checkbox"
                      />
                      <FormattedMessage
                        id="addEditUnitForm.unitHasParentUnit"
                        defaultMessage="This unit has a Parent Unit"
                        description="Checkbox to say that the unit being created has a parent unit"
                      />
                    </label>
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      onClick={() =>
                        displayParentUnitHelpTextRef.current.toggleHelpText()
                      }
                      toggled={showDisplayParentUnitHelpText}
                      styles="margin-left-5"
                    />
                  </div>
                </div>
                <InlineHelpText
                  ref={displayParentUnitHelpTextRef}
                  toggleFunction={toggleDisplayParentUnitHelpText}
                  message={displayParentUnitHelpText}
                />
              </div>
              <div className="content-display__column content-display__column--1-2" />

              <Condition when="hasParentUnit" is={true}>
                <div className="content-display__column">
                  <div className="content-display__item-container">
                    <div className="item-container__main-container">
                      <div className="item-container__main-text text-large text-light">
                        <label>
                          <FormattedMessage
                            id="addEditUnitForm.parentUnitLabel"
                            defaultMessage="Parent Unit:"
                            description="The parent unit of the unit being created"
                          />
                        </label>
                        <HelpIcon
                          onClick={() =>
                            parentUnitHelpTextRef.current.toggleHelpText()
                          }
                          toggled={showParentUnitHelpText}
                          styles="margin-left-5"
                        />
                      </div>
                      <InlineHelpText
                        ref={parentUnitHelpTextRef}
                        toggleFunction={toggleParentUnitHelpText}
                        message={parentUnitHelpText}
                      />
                      <div className="item-container__secondary-text">
                        {selectedUnitTable(values)}

                        <div className="table__header padding-10">
                          <label
                            className="text-medium text-bold"
                            htmlFor="parentFilter">
                            {searchText}:
                          </label>

                          <input
                            type="text"
                            id="parentFilter"
                            name="parentFilter"
                            onChange={searchFilter}
                            className="message-table__filter-search-box margin-left-10"
                            value={parentFilter}
                          />
                        </div>

                        {parentUnitTable(values)}
                      </div>
                    </div>
                  </div>
                </div>
              </Condition>
              <hr />
              <div className="form__section-container form__section-container--spread width-100">
                <div className="form__items-group-container" />
                <div className="form__items-group-container">
                  <Link
                    className="link link-text"
                    to={props.previous}
                    data-test="add-unit-form-cancel-link">
                    {cancelText}
                  </Link>
                  <Button
                    buttonFunction={buttonFunctions.submit}
                    text={submitText}
                    buttonType={buttonTypes.success}
                    buttonSize={buttonSizes.small}
                    buttonTextSize={buttonTextSizes.mediumSmall}
                    additionalStyles="no-margin margin-left-15"
                    disabled={pristine || invalid}
                  />
                </div>
              </div>
            </div>
          </form>
        )}
      />
    </React.Fragment>
  );
};

const mapStateToProps = (state, ownProps) => {
  const unitArray = Object.values(state.units.units);

  return {
    unitObj: state.units.units,
    unitArray,
  };
};

export default connect(mapStateToProps)(UnconnectedAddEditUnitForm);
